import { ArrowLeftOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { Button, Card, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { t } from "../../translations";
import { createLogger } from "../../utils/logger";
import "./SubscriptionSuccessPage.css";

const { Title, Paragraph, Text } = Typography;

// Constants
const SUBSCRIPTION_PRICE = 24.99;

const logger = createLogger("SubscriptionSuccessPage");
const SubscriptionSuccessPage = () => {
  const history = useHistory();
  const location = useLocation();
  const user = useSelector((state) => state.user);
  const lang = useSelector((state) => state.lang);

  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if this is a subscription success redirect
    const urlParams = new URLSearchParams(location.search);
    const subscriptionParam = urlParams.get("subscription");

    if (subscriptionParam === "success") {
      // Fetch updated subscription status
      fetchSubscriptionStatus();
    } else {
      // If not a subscription success, redirect to home
      history.push("/chef/iam-a-chef/home");
    }
  }, [location.search, history]);

  const fetchSubscriptionStatus = async () => {
    try {
      const { MakeRequestAsync } = await import("../functions/axios");
      const { GET } = await import("../constants/request-type");
      const { service_api } = await import("../constants/url");

      const request_details = {
        type: GET,
        url: service_api,
        route: `subscription/chef/${user?._id}/status`,
        token: user?.refresh_token
      };

      const response = await MakeRequestAsync(request_details);
      setSubscriptionStatus(response.data);
      setLoading(false);
    } catch (error) {
      logger.error("Failed to fetch subscription status:", error);
      setLoading(false);
    }
  };

  const handleGoToDashboard = () => {
    history.push("/chef/iam-a-chef/home");
  };

  if (loading) {
    return (
      <div className="subscription-success-container">
        <div className="subscription-success-loading">
          <div className="loading-spinner"></div>
          <Text>{t("subscription.loading_status", lang)}</Text>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-success-container">
      <div className="subscription-success-content">
        {/* Success Header */}
        <Card className="subscription-success-card">
          <Space
            direction="vertical"
            size="large"
            style={{ width: "100%", textAlign: "center" }}
          >
            <CheckCircleOutlined
              style={{
                fontSize: "64px",
                color: "#52c41a",
                marginBottom: "16px"
              }}
            />

            <Title level={2} style={{ color: "#52c41a", margin: 0 }}>
              {t("subscription.success_title", lang)}
            </Title>

            <Paragraph style={{ fontSize: "16px", margin: 0 }}>
              {t("subscription.success_message", lang)}
            </Paragraph>

            {subscriptionStatus && (
              <div className="subscription-details">
                <Text strong>
                  {t("subscription.plan_active", lang)}: ${SUBSCRIPTION_PRICE}
                  /month
                </Text>
                {subscriptionStatus.subscription?.currentPeriodEnd && (
                  <Text
                    type="secondary"
                    style={{ display: "block", marginTop: "8px" }}
                  >
                    {t("subscription.next_billing", lang)}:{" "}
                    {new Date(
                      subscriptionStatus.subscription.currentPeriodEnd
                    ).toLocaleDateString()}
                  </Text>
                )}
              </div>
            )}
          </Space>
        </Card>

        {/* Additional Information */}
        <Card className="subscription-info-card">
          <Title level={5}>{t("subscription.what_happens_next", lang)}</Title>
          <ul className="subscription-info-list">
            <li>{t("subscription.info_1", lang)}</li>
            <li>{t("subscription.info_2", lang)}</li>
            <li>{t("subscription.info_3", lang)}</li>
            <li>{t("subscription.info_4", lang)}</li>
          </ul>
        </Card>

        {/* Action Buttons */}
        <Card className="subscription-actions-card">
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              onClick={handleGoToDashboard}
            >
              {t("common.go_back", lang)}
            </Button>
          </Space>
        </Card>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
